import React, { useState } from 'react';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText, Typography, Box, useTheme as useMuiTheme, useMediaQuery, Divider
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ExploreIcon from '@mui/icons-material/Explore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useLocation } from 'react-router-dom';
import useTheme from '../hooks/useTheme';

const drawerWidth = 220;

const menu = [
  {
    text: 'DASHBOARD',
    icon: <DashboardIcon />,
    link: '/dashboard',
  },
  {
    text: 'USERS',
    icon: <PeopleIcon />,
    link: '/customers',
  },
  {
    text: 'DISCOVERY',
    icon: <ExploreIcon />,
    link: '/discovery',
  },
  {
    text: 'REPORTS',
    icon: <AssessmentIcon />,
    link: '/reports',
  },
  {
    text: 'TESTING',
    icon: <ScienceIcon />,
    link: '/testing',
  },
  {
    text: 'SETTINGS',
    icon: <SettingsIcon />,
    link: '/settings',
  },
  {
    text: 'FEEDBACK',
    icon: <FeedbackIcon />,
    link: '/feedback',
  },
];

export default function Sidebar({ onLogout }) {
  const muiTheme = useMuiTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawerContent = (
    <Box sx={{ 
      height: '100%', 
      background: theme.backgroundColor, 
      color: theme.textColor, 
      display: 'flex', 
      flexDirection: 'column', 
      borderRight: `1px solid ${theme.borderColor}`,
      overflowX: 'hidden',
      overflowY: 'auto'
    }}>
      <Box sx={{ 
        height: 64, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontWeight: 'bold', 
        fontSize: 22, 
        letterSpacing: 1, 
        mb: 2, 
        color: theme.textColor 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ 
            width: 28, 
            height: 28, 
            borderRadius: '50%', 
            background: theme.isDarkMode ? '#2d4b7a' : '#e3f0ff', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mr: 1 
          }}>
            <DashboardIcon sx={{ color: theme.primaryColor, fontSize: 20 }} />
          </Box>
          LOGO
        </Box>
      </Box>
      <List disablePadding sx={{ mt: 2 }}>
        {menu.map((item) => {
          const active = location.pathname === item.link || (item.link === '/dashboard' && location.pathname === '/');
          return (
            <ListItem
              button
              key={item.text}
              component={Link}
              to={item.link}
              sx={{
                color: active ? theme.textColor : theme.textSecondaryColor,
                fontWeight: active ? 700 : 500,
                background: active ? theme.cardBackground : 'transparent',
                borderRadius: active ? '24px' : 2,
                mx: 2,
                my: 0.5,
                height: 44,
                boxShadow: active ? '0 4px 24px 0 rgba(30,34,40,0.08)' : 'none',
                transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
                '&:hover': {
                  background: active ? theme.cardBackground : theme.hoverColor,
                  color: theme.textColor,
                },
                pl: 3,
              }}
            >
              <ListItemIcon sx={{ 
                color: active ? theme.secondaryColor : theme.textSecondaryColor, 
                minWidth: 36, 
                transition: 'color 0.2s cubic-bezier(.4,0,.2,1)' 
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: 16 }} 
              />
            </ListItem>
          );
        })}
      </List>
      
      {/* Logout Section */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ mb: 2, borderColor: theme.borderColor }} />
        <ListItem
          button
          onClick={onLogout}
          sx={{
            color: theme.textSecondaryColor,
            fontWeight: 500,
            borderRadius: 2,
            height: 44,
            transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
            '&:hover': {
              background: '#ffebee',
              color: '#d32f2f',
            },
            pl: 3,
          }}
        >
          <ListItemIcon sx={{ 
            color: theme.textSecondaryColor, 
            minWidth: 36,
            transition: 'color 0.2s cubic-bezier(.4,0,.2,1)',
            '&:hover': {
              color: '#d32f2f'
            }
          }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="LOGOUT" 
            primaryTypographyProps={{ fontWeight: 500, fontSize: 16 }} 
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={() => setMobileOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: theme.backgroundColor,
          color: theme.textColor,
          borderRight: `1px solid ${theme.borderColor}`,
          overflowX: 'hidden',
        },
        display: { xs: isMobile ? 'block' : 'none', md: 'block' },
      }}
    >
      {drawerContent}
    </Drawer>
  );
} 